/**
 * Horizontal bar row — the composition, turnover and span panels are all built
 * from these. Not a chart library: a fixed label / bar / value grid keeps the
 * rows legible at small sizes and lets a single row be faded independently,
 * which is exactly what the employee basis needs for sessional categories.
 */
import React, { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface BarRowProps {
  label: ReactNode;
  /** null renders as an em dash — a suppressed or missing cell is never a 0-width bar with "0". */
  value: number | null;
  max: number;
  color: string;
  /** Visible but de-emphasised: in scope of the data, out of scope of the basis. */
  faded?: boolean;
  valueText?: ReactNode;
  labelWidth?: number;
}

export const BarRow: React.FC<BarRowProps> = ({
  label,
  value,
  max,
  color,
  faded,
  valueText,
  labelWidth = 168,
}) => {
  const pct = value !== null && max > 0 ? Math.max(0.8, (value / max) * 100) : 0;
  return (
    <div
      className={cn(
        'grid items-center gap-2.5 py-[3px] text-[12.5px]',
        faded && 'opacity-[0.38]',
      )}
      style={{ gridTemplateColumns: `minmax(0, ${labelWidth}px) 1fr 62px` }}
    >
      <div className="truncate text-gray-700">{label}</div>
      <div className="relative h-[13px] overflow-hidden rounded-sm bg-gray-100">
        {value !== null ? (
          <span
            className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-300"
            style={{ width: `${pct}%`, background: color }}
          />
        ) : null}
      </div>
      <div className="text-right font-semibold tabular-nums text-heritage-blue">
        {valueText ?? (value === null ? '—' : value.toLocaleString('en-GB'))}
      </div>
    </div>
  );
};
